<!--
  Component: LinkExternalUrl

  This component creates a link to a URL that opens in a new window (may be
  different app on same domain or to an external site).  Also displays URL for
  print version as needed, and allows for tracking clicks.

  Notes:
  - assumes url beginning with / is root-relative
  - path-relative urls are not supported (can't differentiate from urls without
      protocol)
  - http is added to url if not present for href
  - slotted text is shown as display/clickable portion, if available
  - printing of url:
    - if there is a value in the the default slot (passed in from outside to
      override displaying url), the url will be printed in parenthesis
    - if no value in default slot (url displayed on screen already), no
      additional url is displayed for printing
  - url is displayed if no slot text is available, with http(s) removed
  - Link is opened in a new tab.  Noopener/noreferrer to prevent tabjacking
    - root-relative urls don't use noopener/noreferrer

  Props:
  - url - url to be linked to
  - labelId - (optional) id of element with text that labels current link for
      screenreader (for when no slot text is supplied, otherwise will be read
      instead of slotted text)
  - showPrintLink - flag to allow turning off the default behavior of showing
      url in parenthesis for printing
-->

<template>
  <a :href="urlForHref"
    class="ext-link no-print-link"
    :aria-labelledby="labelId"
    target="_blank"
    :rel="!isRootRelativeUrl ? 'noopener noreferrer' : ''"
    @click="linkClicked"
    >
    <slot>{{urlNoProtocol}}</slot><span v-if="hasDefaultSlot && showPrintLink" class="print-only-inline"> ({{urlNoProtocol}})</span>
  </a>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { logEvent } from '@/helpers/google-analytics'

// Removing http[s] from here: https://stackoverflow.com/a/3999810
const re = /^https?:\/\//i

@Component({
  props: {
    url: {
      type: String,
      required: true
    },
    labelId: String,
    showPrintLink: {
      type: Boolean,
      default: true
    }
  }
})
export default class LinkExternalUrl extends Vue {
  /* Getters */

  get hasDefaultSlot () {
    return !!this.$slots.default
  }
  get isRootRelativeUrl () {
    return this.url.length > 0 && this.url[0] === '/'
  }
  get urlNoProtocol () {
    return this.urlProtocol.replace(re, '')
  }
  get urlProtocol () {
    if (this.isRootRelativeUrl) {
      const u = new URL(this.url, this.$APP_PUBLIC_BASE_URL)
      return u.href
    }
    return re.test(this.url) ? this.url : 'http://' + this.url
  }
  get urlForHref () {
    return this.isRootRelativeUrl ? this.url : this.urlProtocol
  }

  /* Event Handlers */

  linkClicked () {
    // Track http/https version for external websites, and root-relative links
    //  for other apps on the same site
    logEvent(this, 'Link', 'linkClicked', this.urlForHref)
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
a span {
  overflow-wrap: break-word;
}
</style>



// WEBPACK FOOTER //
// src/components/LinkExternalUrl.vue